import { Dialog } from 'radix-ui'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { Button } from './button'

/**
 * Modal de confirmação — componente 09. Sempre com o efeito escrito.
 * Título com o objeto entre aspas ("Excluir 'Preenchimento labial'?"), um parágrafo com o efeito real
 * e o que NÃO se perde, alternativa menos destrutiva quando existir, e três ações:
 * cancelar em texto, alternativa em contorno, destrutiva em plum.
 * "Esta ação não pode ser desfeita" como única explicação é proibido.
 * Esc fecha; o foco volta ao elemento que abriu (Radix cuida disso).
 */

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** O efeito real da ação e o que não se perde. */
  effect: ReactNode
  /** Alternativa menos destrutiva ("Alternativa: arquivar. Some do catálogo, mas volta a qualquer momento…"). */
  alternative?: {
    description: ReactNode
    label: string
    onSelect: () => void
  }
  confirmLabel: string
  onConfirm: () => void
  cancelLabel?: string
  /** Enquanto a ação roda, o botão destrutivo mostra este rótulo e bloqueia. */
  loading?: boolean
  loadingLabel?: string
  /** Para confirmações não destrutivas (ex.: "Sair sem salvar?"), a ação principal fica em ink. */
  tone?: 'destructive' | 'primary'
  /** Um campo entre o efeito e as ações (o motivo, a justificativa), quando a regra exige. */
  children?: ReactNode
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  effect,
  alternative,
  confirmLabel,
  onConfirm,
  cancelLabel = 'Cancelar',
  loading,
  loadingLabel,
  tone = 'destructive',
  children,
}: ConfirmDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-overlay data-[state=open]:animate-in data-[state=open]:fade-in fixed inset-0 z-40" />
        <Dialog.Content
          data-slot="confirm-dialog"
          className={cn(
            'border-hairline-heavy bg-pampas shadow-float fixed top-1/2 left-1/2 z-50 w-[calc(100%-48px)] max-w-[470px] -translate-x-1/2 -translate-y-1/2 border px-7 py-[26px] outline-none',
          )}
        >
          <Dialog.Title className="font-display text-ink text-[20px] leading-[1.25] font-light text-pretty">
            {title}
          </Dialog.Title>
          <Dialog.Description className="font-body text-body text-secondary mt-3 font-light">
            {effect}
          </Dialog.Description>
          {alternative ? (
            <div className="border-lilac bg-surface font-body text-ink mt-4 border-l-2 px-[14px] py-3 text-[12px] leading-[1.6] font-light">
              {alternative.description}
            </div>
          ) : null}
          {children ? <div className="mt-4">{children}</div> : null}
          <div className="mt-5 flex flex-wrap justify-end gap-[10px]">
            <Dialog.Close asChild>
              <Button variant="text" className="border-b-0 px-4 py-3 text-[12px] hover:opacity-80">
                {cancelLabel}
              </Button>
            </Dialog.Close>
            {alternative ? (
              <Button
                variant="outline"
                size="sm"
                className="border-ink px-4 py-3 text-[12px]"
                onClick={alternative.onSelect}
              >
                {alternative.label}
              </Button>
            ) : null}
            <Button
              variant={tone === 'destructive' ? 'destructive' : 'primary'}
              size="sm"
              className="font-body px-4 py-3 text-[12px] font-light"
              onClick={onConfirm}
              loading={loading}
              loadingLabel={loadingLabel}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
